import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../api/client';
import { ChevronRight, ChevronDown, FolderTree, Loader2 } from 'lucide-react';

interface CategoryTreeProps {
  siteId: string;
  sellerId: number;
  selectedCategory: CategoryNode | null;
  onSelect: (cat: CategoryNode) => void;
}

export interface CategoryNode {
  id: string;
  name: string;
  total_items?: number;
  children_count?: number;
}

interface TreeNodeState {
  category: CategoryNode;
  children: TreeNodeState[];
  expanded: boolean;
  loading: boolean;
  loaded: boolean;
}

function SkeletonTree() {
  return (
    <div className="space-y-2 py-2">
      {[1, 2, 3, 4, 5, 6].map(i => (
        <div key={i} className="flex items-center gap-2" style={{ paddingLeft: `${(i % 3) * 16}px` }}>
          <div
            className="w-4 h-4 rounded animate-pulse"
            style={{ backgroundColor: 'var(--color-bg-tertiary)' }}
          />
          <div
            className="h-4 rounded animate-pulse"
            style={{
              width: `${100 + Math.random() * 80}px`,
              backgroundColor: 'var(--color-bg-tertiary)',
            }}
          />
        </div>
      ))}
    </div>
  );
}

function TreeItem({
  node,
  depth,
  selectedId,
  siteId,
  sellerId,
  onSelect,
  onToggle,
}: {
  node: TreeNodeState;
  depth: number;
  selectedId: string | null;
  siteId: string;
  sellerId: number;
  onSelect: (cat: CategoryNode) => void;
  onToggle: (nodeId: string) => void;
}) {
  const isSelected = selectedId === node.category.id;
  const hasChildren = (node.category.children_count ?? 0) > 0 || node.children.length > 0;

  return (
    <div>
      <div
        className={`flex items-center gap-1 py-1.5 px-2 rounded-md cursor-pointer text-sm transition-colors
          ${isSelected ? 'font-semibold' : 'hover:opacity-80'}`}
        style={{
          paddingLeft: `${depth * 16 + 8}px`,
          backgroundColor: isSelected ? 'var(--color-bg-tertiary)' : 'transparent',
          color: isSelected ? 'var(--color-primary)' : 'var(--color-text)',
        }}
        onClick={() => onSelect(node.category)}
      >
        {/* Expand/Collapse chevron */}
        {hasChildren ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggle(node.category.id);
            }}
            className="p-0.5 rounded hover:opacity-70 flex-shrink-0"
            style={{ color: 'var(--color-text-tertiary)' }}
          >
            {node.loading ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : node.expanded ? (
              <ChevronDown className="w-3.5 h-3.5" />
            ) : (
              <ChevronRight className="w-3.5 h-3.5" />
            )}
          </button>
        ) : (
          <span className="w-4.5 flex-shrink-0" />
        )}

        {/* Category name */}
        <span className="truncate flex-1">{node.category.name}</span>

        {/* Item count */}
        {node.category.total_items != null && (
          <span
            className="text-xs ml-1 flex-shrink-0"
            style={{ color: 'var(--color-text-tertiary)' }}
          >
            ({node.category.total_items.toLocaleString()})
          </span>
        )}
      </div>

      {/* Children */}
      {node.expanded && node.children.length > 0 && (
        <div>
          {node.children.map(child => (
            <TreeItem
              key={child.category.id}
              node={child}
              depth={depth + 1}
              selectedId={selectedId}
              siteId={siteId}
              sellerId={sellerId}
              onSelect={onSelect}
              onToggle={onToggle}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function CategoryTree({ siteId, sellerId, selectedCategory, onSelect }: CategoryTreeProps) {
  const { t } = useTranslation();
  const [tree, setTree] = useState<TreeNodeState[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch root categories
  const fetchRoot = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<{ categories: CategoryNode[] }>(
        `/publisher/discover/tendencias/categories?site_id=${siteId}&seller_id=${sellerId}`
      );
      if (res.success && res.data) {
        setTree(
          (Array.isArray(res.data) ? res.data : (res.data as any)?.categories || []).map((cat: any) => ({
            category: cat,
            children: [],
            expanded: false,
            loading: false,
            loaded: false,
          }))
        );
      }
    } catch (err) {
      console.error('Failed to fetch categories', err);
    } finally {
      setLoading(false);
    }
  }, [siteId, sellerId]);

  useEffect(() => {
    fetchRoot();
  }, [fetchRoot]);

  // Toggle expand/collapse with lazy load
  const handleToggle = useCallback(async (nodeId: string) => {
    const toggleInTree = (nodes: TreeNodeState[]): TreeNodeState[] =>
      nodes.map(n => {
        if (n.category.id === nodeId) {
          // If already loaded, just toggle
          if (n.loaded) {
            return { ...n, expanded: !n.expanded };
          }
          // Need to fetch children
          return { ...n, loading: true, expanded: true };
        }
        if (n.children.length > 0) {
          return { ...n, children: toggleInTree(n.children) };
        }
        return n;
      });

    setTree(prev => toggleInTree(prev));

    // Check if we need to fetch
    const findNode = (nodes: TreeNodeState[]): TreeNodeState | null => {
      for (const n of nodes) {
        if (n.category.id === nodeId) return n;
        const found = findNode(n.children);
        if (found) return found;
      }
      return null;
    };

    const current = findNode(tree);
    if (current && !current.loaded) {
      try {
        const res = await api.get<{ categories: CategoryNode[] }>(
          `/publisher/discover/tendencias/categories/${nodeId}/children?seller_id=${sellerId}`
        );
        if (res.success && res.data) {
          const updateChildren = (nodes: TreeNodeState[]): TreeNodeState[] =>
            nodes.map(n => {
              if (n.category.id === nodeId) {
                return {
                  ...n,
                  loading: false,
                  loaded: true,
                  children: (Array.isArray(res.data) ? res.data : (res.data as any)?.categories || []).map((cat: any) => ({
                    category: cat,
                    children: [],
                    expanded: false,
                    loading: false,
                    loaded: false,
                  })),
                };
              }
              if (n.children.length > 0) {
                return { ...n, children: updateChildren(n.children) };
              }
              return n;
            });
          setTree(prev => updateChildren(prev));
        }
      } catch (err) {
        console.error('Failed to fetch children', err);
        // Reset loading state
        const resetLoading = (nodes: TreeNodeState[]): TreeNodeState[] =>
          nodes.map(n => {
            if (n.category.id === nodeId) return { ...n, loading: false, expanded: false };
            if (n.children.length > 0) return { ...n, children: resetLoading(n.children) };
            return n;
          });
        setTree(prev => resetLoading(prev));
      }
    }
  }, [tree, sellerId]);

  return (
    <div
      className="rounded-lg border overflow-hidden flex flex-col"
      style={{
        borderColor: 'var(--color-border)',
        backgroundColor: 'var(--color-bg-secondary)',
        maxHeight: '600px',
      }}
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b" style={{ borderColor: 'var(--color-border)' }}>
        <FolderTree className="w-4 h-4" style={{ color: 'var(--color-primary)' }} />
        <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text)' }}>
          {t('tendencias.categories')}
        </h3>
      </div>

      {/* Tree */}
      <div className="overflow-y-auto flex-1 py-1">
        {loading ? (
          <div className="px-2">
            <SkeletonTree />
          </div>
        ) : tree.length === 0 ? (
          <p className="text-sm py-8 text-center" style={{ color: 'var(--color-text-tertiary)' }}>
            {t('tendencias.noCategories')}
          </p>
        ) : (
          tree.map(node => (
            <TreeItem
              key={node.category.id}
              node={node}
              depth={0}
              selectedId={selectedCategory?.id ?? null}
              siteId={siteId}
              sellerId={sellerId}
              onSelect={onSelect}
              onToggle={handleToggle}
            />
          ))
        )}
      </div>
    </div>
  );
}
